import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ErrorMessageBar } from '../../components/Core/ErrorMessageBar';
import FolderPermissionsView from '../../components/PermissionsView/FolderPermissionsView';

type FolderSearch = {
  // Exchange folder id of the selected folder. Every permission endpoint needs it to address the
  // folder in Exchange, so it is only optional here because a URL can always be incomplete.
  exchangeFolderId?: string;
  // Display name of the selected folder. The folder tree already knows it, so the permission
  // endpoints do not have to return it.
  folderName?: string;
};

export const Route = createFileRoute('/_auth/folder/$folderId')({
  validateSearch: (search: Record<string, unknown>): FolderSearch => ({
    exchangeFolderId: typeof search.exchangeFolderId === 'string' ? search.exchangeFolderId : undefined,
    folderName: typeof search.folderName === 'string' ? search.folderName : undefined,
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { folderId: graphFolderId } = Route.useParams();
  const { exchangeFolderId, folderName } = Route.useSearch();
  const { t } = useTranslation();

  // Navigating from the folder tree always carries the Exchange folder id along; it is missing only
  // when the URL was typed or bookmarked without it. Saying so here is better than letting the view
  // send a request the API is bound to reject.
  if (!exchangeFolderId) {
    return (
      <ErrorMessageBar
        error={{ message: t('permissions.errors.missingExchangeFolderId') }}
        messageBarTitle={t('permissions.errors.couldNotLoadError')}
      />
    );
  }

  return <FolderPermissionsView graphFolderId={graphFolderId} exchangeFolderId={exchangeFolderId} folderName={folderName} />;
}
